//@author: mandy

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    effect,
    ElementRef,
    EventEmitter,
    HostListener,
    input,
    Input,
    model,
    NgZone,
    OnDestroy,
    OnInit,
    Output,
    untracked,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { AngBridgeUtil } from "../../util/ang.bridge.util";
import { S25Const } from "../../util/s25-const";
import { S25Util } from "../../util/s25-util";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Bind } from "../../decorators/bind.decorator";
import { TinymceLoader } from "../tinymce/tinymce.loader";
import { Editor } from "../../assets/tinymce/tinymce.d";

const menuItems = ["file", "insert", "table", "view", "format", "tools", "help"] as const;
type MenuItem = (typeof menuItems)[number];

@TypeManagerDecorator("s25-ng-rich-text-editor")
@Component({
    selector: "s25-ng-rich-text-editor",
    template: `
        <div #wrapper>
            @if (this.rteLabel) {
                <label [for]="this.id" class="ngBold">{{ this.rteLabel }}</label>
            }
            @defer (on idle; on viewport) {
                <textarea #textAreaElement [id]="id" [attr.aria-label]="ariaLabel()"></textarea>
            } @placeholder {
                <!-- Carries the id too, so an rteLabel points at something while the editor loads. -->
                <textarea [id]="id" [attr.aria-label]="ariaLabel()"></textarea>
            }
        </div>
    `,
    styles: `
        :host ::ng-deep .tox-statusbar__help-text {
            flex-basis: 100% !important;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25RichTextEditorComponent implements OnInit, OnDestroy {
    readonly modelValue = model<string | undefined>();
    @Input() rteLabel: any = undefined;
    readonly ariaLabel = input<string | undefined>();
    @Input() autoFocus: boolean = false;
    @Input() autoResize: boolean = false;
    @Input() defaultHeight?: number;
    @Input() updateOn: "change" | "keyup" = "change";
    @Input() resize: boolean | "both" = "both"; // https://www.tiny.cloud/docs/tinymce/latest/editor-size-options/#resize
    readonly disabled = input<boolean | undefined>();
    @Output() onEditorContentChange = new EventEmitter<any>();
    readonly menubar = input<MenuItem[]>(menuItems.slice());

    readonly textarea = viewChild<ElementRef<HTMLTextAreaElement>>("textAreaElement");

    static count: number = 1;
    editor: Editor | undefined;
    readonly id = `ngTextArea-${S25RichTextEditorComponent.count++}`;
    private lastEmittedContent: string | undefined;
    private componentDestroyed = false;
    private tinymceInitInFlight = false;

    constructor(
        private cd: ChangeDetectorRef,
        private zone: NgZone,
    ) {
        AngBridgeUtil.init(this);

        effect(() => {
            if (this.textarea()) untracked(() => this.init());
        });

        effect(() => {
            const newValue = this.modelValue();
            if (newValue === this.lastEmittedContent) return;
            if (this.editor && this.editor.getContent() !== newValue) {
                this.editor.setContent(newValue ?? "");
            }
        });

        effect(() => {
            const isDisabled = this.disabled();
            if (TinymceLoader.isLoaded() && this.editor) {
                try {
                    const mode = isDisabled ? "readonly" : "edit";
                    this.editor?.mode?.set?.(mode);
                } catch {}
            }
        });
    }

    ngOnInit() {
        this.cd.detectChanges();
    }

    init() {
        if (this.componentDestroyed || this.tinymceInitInFlight || this.editor) return;
        if (!this.textarea()) return;
        this.tinymceInitInFlight = true;
        const autoResize = this.autoResize; // temp store this value because TinymceLoader.load() seems to access previous value
        TinymceLoader.load()
            .then(() => {
                this.tinymceInitInFlight = false;
                if (this.componentDestroyed) return;
                this.autoResize = autoResize; // for some reason TinymceLoader.load() either resets the value for autoResize or doesn't update with the passed-in value from s25-ng-editable-richtext
                this.zone.runOutsideAngular(() => {
                    window.tinymce
                        .init({
                            selector: "#" + this.id,
                            readonly: !!this.disabled(),
                            element_format: "xhtml", //ANG-7081 reports need xhtml format https://www.tiny.cloud/docs/tinymce/latest/content-filtering/#element_format
                            cache_suffix: `?v=${TinymceLoader.tinymceVersion}`,
                            relative_urls: false,
                            remove_script_host: false,
                            convert_urls: true,
                            elementpath: false,
                            branding: false,
                            promotion: false,
                            link_assume_external_targets: "https",
                            browser_spellcheck: true,
                            auto_focus: S25Util.toBool(this.autoFocus) ? this.id : null,
                            contextmenu: "spellcheck image table",
                            menubar: this.menubar().join(" "), //take out "edit" option since it causes an empty popup and does not add much
                            plugins: "link code image table lists help" + (this.autoResize ? " autoresize" : ""),
                            toolbar:
                                "undo redo blocks | bold italic underline| forecolor backcolor | numlist bullist | fontfamily fontsize | link",
                            statusbar: true, //note: required for resize to work
                            resize: this.resize ?? "both",
                            highlight_on_focus: true,
                            iframe_aria_text: "Rich Text Editor. Press ALT-0 for keyboard shortcuts.",
                            help_accessibility: true,
                            // iOS app fix: TinyMCE 6+ uses srcdoc for iframe content which fails
                            // in iOS WKWebView. init_content_sync reverts to TinyMCE 5 style loading.
                            // See: https://github.com/tinymce/tinymce/issues/8362
                            ...(S25Const.isIOSApp && {
                                init_content_sync: true,
                                iframe_attrs: { ontouchstart: "" },
                                mobile: {
                                    menubar: true,
                                    toolbar_mode: "sliding",
                                    resize: true,
                                },
                            }),
                            // Android app: use sliding toolbar to show overflow button
                            ...(S25Const.isAndroidApp && {
                                mobile: {
                                    toolbar_mode: "sliding",
                                },
                            }),
                            setup: (editor: Editor) => {
                                this.editor = editor;
                                editor.on("input", this.onChange);
                                editor.on("keyup", this.onKeyUp);

                                // iOS app iframe focus workaround: iOS WKWebView has a bug where
                                // iframes lose focus or fail to capture touch events. Re-focusing the
                                // content window on touch/key events fixes this.
                                if (S25Const.isIOSApp) {
                                    editor.on("PostRender", () => {
                                        const iframe = editor.iframeElement;
                                        if (!iframe?.contentWindow || !iframe?.contentDocument) return;

                                        const refocus = () => iframe.contentWindow?.focus();

                                        iframe.contentDocument.addEventListener("touchstart", refocus);
                                        iframe.contentDocument.addEventListener("touchend", refocus);
                                        iframe.contentDocument.addEventListener("keydown", refocus);
                                    });
                                }
                            },
                            init_instance_callback: (editor: Editor) => {
                                if (this.componentDestroyed) return;
                                const value = this.modelValue();
                                if (value) editor.setContent(value);
                            },
                            ...(this.defaultHeight && { height: this.defaultHeight }),
                            urlconverter_callback: (url: string, node: any, on_save: true, name: string) => {
                                // ANG-4306 If link is a link variable don't add "https://"
                                if (url.toLowerCase() === "https://{{$pro.vars.eventurl}}") return url.slice(8);
                                return url;
                            },
                        })
                        // tinymce.init returns Promise<Editor[]> whose rejection we MUST consume.
                        // When the host <textarea> is removed from the DOM between this call and
                        // the editor's body being attached (e.g. parent panel closed/reopened
                        // quickly), TinyMCE's nodeChanged -> editor.getBody() throws "Node cannot
                        // be null or undefined" from inside its own async render. Without this
                        // .catch, zone.js reports that as an unhandled rejection. The orphan
                        // editor is harmless; ngOnDestroy below cleans it up via tinymce.remove.
                        .catch(() => {});
                });

                this.cd.detectChanges();
            })
            .catch(() => {
                this.tinymceInitInFlight = false;
            });
    }

    // ANG-4971 Take advantage of the buttons being in the page, but the editor in an iframe
    @HostListener("window:focus")
    @Bind
    onChange() {
        // Always update on change
        this.update();
    }

    @Bind
    onKeyUp() {
        if (this.updateOn !== "keyup") return;
        this.update();
    }

    update() {
        const content = this.editor?.getContent();
        if (content !== undefined) {
            this.lastEmittedContent = content;
            this.zone.run(() => {
                this.onEditorContentChange.emit(content);
                this.modelValue.set(content);
            });
        }
    }

    ngOnDestroy() {
        this.componentDestroyed = true;
        // Cover both the "setup already fired" path (this.editor is set) and the "setup not yet
        // fired but TinyMCE registered the editor under this.id" path so we never leak an editor
        // whose listeners would keep firing on window resize / DOM events.
        const editorToRemove = this.editor || window.tinymce?.get?.(this.id);
        if (editorToRemove) {
            try {
                window.tinymce.remove(editorToRemove);
            } catch {}
            this.editor = undefined;
        }
    }
}
